import {
  ACTION_ARGS,
  ANCHORED_ARGS,
  Arg,
  COMPONENT_STATE_ARGS,
  CONTENT_ARGS,
  INPUT_MENU_ARGS,
  INPUT_SELECT_ALL_ARGS,
  MENU_PLACEMENT_ARG,
  Metadata,
  SIZING_ARGS,
  pick,
} from '../../../utils/spds/metadata';
import { Animated } from '../core/capability/animated.gjs';
import { HIDDEN_SELECTOR } from '../../../utils/keyboard-nav-support';
import { compose } from '../../../utils/spds/capabilities/helpers';
import { ensureSafeComponent } from '@embroider/util';
import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { reads } from 'macro-decorators';
import { tracked } from '@glimmer/tracking';
import { waitForNext } from '../../../utils/waiters';
import CheckboxGroup from '../input/group/checkbox.gjs';
import ExpandableItem from './multiselect/expandable-item.gjs';
import FlexRow from '../layout/flex/row.gjs';
import GhostButton from '../action/button/ghost.gjs';
import Menu from './filterable.gjs';
import NoResults from '../core/menu/no-results.gjs';
import PrimaryButton from '../action/button/primary.gjs';
import Super from '../core/base.gjs';
import boldMatchingText from '../../../modifiers/bold-matching-text';
import styles from './multiselect.module.scss';

const Item = <template>
  <@checkbox
    @checked={{@checked}}
    @disabled={{@disabled}}
    @id={{@id}}
    @indeterminate={{@indeterminate}}
    @inputClasses={{@inputClasses}}
    @label={{@label}}
    @tabIndex="-1"
    @value={{@value}}
    {{boldMatchingText @searchValue}}
    ...attributes
  >
    {{yield}}
  </@checkbox>
</template>;

const METADATA = Metadata.define({
  description: `
Unified multiselect menu shared by \`Spds::Input::Dropdown::List\` and \`Spds::Input::Search\`. It composes
\`Spds::Menu::Filterable\` with a \`Spds::Input::Group::Checkbox\`, adds an automatic "Select all" option, and
optionally an \`expandableItem\` (accordion), an opt-in footer (Apply + Clear all), and additive selection
notification hooks.

### Search modes
- When no \`@searchValue\` is supplied (dropdown usage), the menu's built-in filterable search is used
  (\`@searchable\`/\`@searchThreshold\`/\`@searchPlaceholder\`).
- When \`@searchValue\` is supplied (search-input usage), the parent owns filtering; the built-in search is
  disabled and matches are highlighted via the yielded \`item\`.

### Footer
When \`@onApply\` and/or \`@showClearAll\` are set, a footer renders below the scrollable list. Apply commits the
current checked values via \`@onApply(values)\`; Clear all clears the selection (emitting \`@onChange([])\`) and
calls \`@onClearAll\`.`,
  extends: [
    pick(ANCHORED_ARGS, ['anchorElement', 'anchorOffset', MENU_PLACEMENT_ARG]),
    pick(ACTION_ARGS, [
      'onApply',
      'onCancel',
      { name: 'onChange', description: 'Called when the selection changes' },
      'onClearAll',
      { name: 'onClick', required: true },
      'onSelect',
      'onSelectAll',
    ]),
    pick(COMPONENT_STATE_ARGS, [{ name: 'open', required: true }]),
    pick(CONTENT_ARGS, [{ name: 'subtitle', initialValue: 'Subtitle' }]),
    pick(INPUT_MENU_ARGS, [
      'displayedItemCount',
      'noResultsText',
      'retainSelection',
      'searchable',
      'searchPlaceholder',
      'searchThreshold',
    ]),
    pick(INPUT_SELECT_ALL_ARGS, [
      { name: 'selectAllLabel', default: 'Select all' },
      { name: 'selectAllMin', default: 6 },
    ]),
    pick(SIZING_ARGS, ['maxHeight', 'maxWidth', 'minWidth', 'width']),
  ],
  meta: {
    autoFilter: Arg.boolean({
      default: true,
      description:
        'When true, the filterable menu applies its built-in text filter. Set false so a parent owns filtering (custom/async search); the yielded item then handles match highlighting.',
    }),
    applyLabel: Arg.string({ default: 'Apply', description: 'Label for the footer Apply button' }),
    searchValue: Arg.string({
      description: 'External filter value; enables match highlighting and disables built-in search',
    }),
    selectAll: Arg.action({ description: 'Custom select-all handler (search input)' }),
    selected: Arg.array({ description: 'Externally controlled selected checkbox values' }),
    showClearAll: Arg.boolean({
      description:
        'Keeps the clear all button in view even when no selections are made. Only takes effect when the menu has enough items to show the select all control (see `selectAllMin`).',
      defaultValue: false,
    }),
  },
  name: 'Multiselect Menu',
});

export default class SpdsMenuMultiselect extends compose(Super, Animated) {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-menu-multiselect';
  styles = styles;

  @tracked checkboxGroup;

  @reads('checkboxGroup.navigableOptions', []) navigableOptions;
  @reads('checkboxGroup.selectedCount', 0) selectedCount;

  get applyDisabled() {
    return this.showApply && this.selectedCount === 0;
  }

  get applyLabel() {
    return this.argOrDefault('applyLabel');
  }

  get autoFilter() {
    return this.argOrDefault('autoFilter');
  }

  get disableClearAll() {
    return this.showClearAll && this.selectedCount === 0;
  }

  get groupName() {
    return this.composeSubcomponentId('checkbox-group');
  }

  get itemClass() {
    return this.useInternalSearch ? 'spds-input-dropdown-list-item' : 'spds-input-search-menu-item';
  }

  get menuId() {
    return this.composeSubcomponentId('multi-menu');
  }

  get noItemsDisplayed() {
    let { displayedItemCount } = this.args;

    return typeof displayedItemCount === 'number' ? displayedItemCount === 0 : undefined;
  }

  get searchable() {
    return this.useInternalSearch ? this.args.searchable : false;
  }

  get searchValue() {
    return this.autoFilter ? undefined : this.args.searchValue;
  }

  get selectAllLabel() {
    return this.argOrDefault('selectAllLabel');
  }

  get selectAllMin() {
    return this.argOrDefault('selectAllMin');
  }

  get showApply() {
    return !!this.args.onApply;
  }

  get showClearAll() {
    return this.showSelectAll && (this.argOrDefault('showClearAll') || this.selectedCount > 0);
  }

  get showFooter() {
    return this.showApply || this.showSelectAll;
  }

  get showHeaderContent() {
    return this.showSelectAll || this.args.subtitle;
  }

  get showSelectAll() {
    return this.navigableOptions?.length >= this.selectAllMin;
  }

  get useInternalSearch() {
    return this.args.searchValue === undefined;
  }

  get visibleOptions() {
    return this.navigableOptions.filter(
      option => option && !option.parentElement?.classList.contains(HIDDEN_SELECTOR)
    );
  }

  #setVisibleChecked(checked) {
    this.#updateChecked(() => this.visibleOptions.forEach(option => (option.checked = checked)));
  }

  #updateChecked(update) {
    let group = this.checkboxGroup;
    if (!group) return;

    update();
    group.updateItems();

    let values = group.selected.map(option => option.value);

    this.args.onChange?.({ values });
    this.args.onClick?.({ values });
  }

  /**
   * Restores checkboxes to a recorded state and reports the result like any other selection change.
   * Options missing from the snapshot are left as they are.
   * @param {Map<HTMLInputElement, boolean>} snapshot
   */
  restoreChecked(snapshot) {
    this.#updateChecked(() => snapshot.forEach((checked, option) => (option.checked = checked)));
  }

  apply = () => {
    let values = this.checkboxGroup?.selected?.map(option => option.value) ?? [];

    this.args.onApply?.(values);
  };

  clearAll = () => {
    this.#setVisibleChecked(false);

    this.args.onClearAll?.();
  };

  selectAll = event => {
    if (this.args.selectAll) {
      this.args.selectAll({ checked: true, event, groupName: this.groupName });
    } else {
      this.#setVisibleChecked(true);
    }

    this.args.onSelectAll?.({
      checked: true,
      event,
      groupName: this.groupName,
      values: this.checkboxGroup?.selected?.map(option => option.value) ?? [],
    });
  };

  registerCheckboxGroup = async component => {
    await waitForNext();

    if (this.isDestroying || this.isDestroyed) return;

    this.checkboxGroup = component;
  };

  <template>
    <Menu
      @anchorElement={{@anchorElement}}
      @anchorOffset={{@anchorOffset}}
      @animated={{this.animated}}
      @autoFilter={{this.autoFilter}}
      @id={{this.id}}
      @items={{this.navigableOptions}}
      @maxHeight={{@maxHeight}}
      @maxWidth={{@maxWidth}}
      @minWidth={{@minWidth}}
      @multiselect={{true}}
      @noItemsDisplayed={{this.noItemsDisplayed}}
      @noResultsText={{@noResultsText}}
      @open={{@open}}
      @placement={{@placement}}
      @searchable={{this.searchable}}
      @searchPlaceholder={{@searchPlaceholder}}
      @searchThreshold={{@searchThreshold}}
      @searchValue={{@searchValue}}
      @width={{@width}}
      class={{this.classes}}
      data-test-spds-menu-multiselect
      {{this.setupComponent}}
      {{this.validateArguments}}
      ...attributes
    >
      <:header>
        {{#if this.showHeaderContent}}
          <FlexRow @alignItems="center" @justifyContent="space-between">
            <FlexRow
              @flex="1"
              @tag="h2"
              class="spds-menu-multiselect-header-subtitle"
              data-test-spds-menu-multiselect-header-subtitle
            >
              {{@subtitle}}
            </FlexRow>
            {{#if this.showSelectAll}}
              <GhostButton
                data-test-spds-menu-multiselect-header-select-all
                {{on "click" this.selectAll}}
              >
                {{this.selectAllLabel}}
              </GhostButton>
            {{/if}}
          </FlexRow>
        {{/if}}
      </:header>
      <:body as |activeFilter|>
        <CheckboxGroup
          @groupName={{this.groupName}}
          @hideDescription={{true}}
          @id={{this.groupName}}
          @layout="column"
          @onClick={{@onClick}}
          @onSelectAll={{@onSelectAll}}
          @onSetup={{@onClick}}
          @registerComponent={{this.registerCheckboxGroup}}
          @unregisterComponent={{fn this.unregisterComponent "checkboxGroup"}}
          as |cg|
        >
          {{yield
            (hash
              item=(component
                Item
                checkbox=(ensureSafeComponent cg.checkbox)
                inputClasses=this.itemClass
                searchValue=this.searchValue
              )
              expandableItem=(component
                ExpandableItem
                autoFilter=this.autoFilter
                checkbox=(ensureSafeComponent cg.checkbox)
                inputClasses=this.itemClass
                searchValue=activeFilter
              )
              noResults=(component NoResults)
            )
          }}
        </CheckboxGroup>
      </:body>

      <:footer>
        {{#if this.showFooter}}
          <GhostButton
            @alignSelf="start"
            @disabled={{this.disableClearAll}}
            @fitContent={{true}}
            class={{if this.showClearAll "" "invisible"}}
            data-test-spds-menu-multiselect-clear-all
            {{on "click" this.clearAll}}
          >
            Clear all
          </GhostButton>

          <PrimaryButton
            @alignSelf="end"
            @disabled={{this.applyDisabled}}
            @fitContent={{true}}
            @size="small"
            class={{if this.showApply "" "invisible"}}
            data-test-spds-menu-multiselect-apply
            {{on "click" this.apply}}
          >
            {{this.applyLabel}}
          </PrimaryButton>
        {{/if}}
      </:footer>
    </Menu>
  </template>
}

export const SPDS_MENU_MULTISELECT_METADATA = SpdsMenuMultiselect.metadata;
